import React, { useEffect } from 'react';
import { withOktaAuth } from '@okta/okta-react';
import type { OktaContext } from '@okta/okta-react';

type IOktaContext = React.ContextType<typeof OktaContext>;

import Loader from 'components/partials/loader/loader';
import NotificationMessage from 'components/partials/notification/notification';
import SkipLink from 'components/partials/skip-link/skip-link';
import { SideMenuLayout } from 'components/shared/side-menu';
import MainAuthenticated from 'components/shared/others/main-authenticated/main-authenticated';
import Footer from 'components/shared/others/footer/footer';
import OnboardingPopup from 'containers/admin/clients/client/tabs-components/ai-assistant/components/onboarding-popup';

import { useCurrentUser } from 'state/current-user/current-user.thunk';
import usePageTitle from 'hooks/use-page-title';

// TODO: Wave 8 — withOktaAuth HOC injects { oktaAuth, authState }; props match
// the IOktaContext shape Okta passes. The component body doesn't read any of
// those props directly today.
type AuthenticatedAppProps = Partial<IOktaContext>;

const AuthenticatedApp: React.FC<AuthenticatedAppProps> = () => {
  const user = useCurrentUser();

  usePageTitle();

  useEffect(() => {
    if (!user.data || !user.id) {
      user.getInfo();
    }
  }, [user.id, user.data]);

  if (user.error) {
    // TODO: Wave 8 — `user.error` is `unknown` in the Redux slice. The legacy
    // JS reads `.data.detail` / `.data.title` defensively. Cast preserves
    // semantics until the error shape is centralised.
    const userError = user.error as
      | { data?: { detail?: string; title?: string } }
      | undefined;

    return (
      <NotificationMessage
        customClass="m-8"
        variant="error"
        heading="An error occurred"
        message={
          (userError && userError.data && userError.data.detail) ||
          (userError && userError.data && userError.data.title)
        }
      />
    );
  }

  return (
    <>
      <SkipLink />
      {user.loading || !user.id || !user.data ? (
        <Loader loading />
      ) : (
        <SideMenuLayout>
          <div className="w-full flex-1 flex flex-col font-text font-normal leading-normal text-base p-0 text-gray-700">
            <div className="flex flex-1 pb-6">
              {/* TODO: Wave 8 — MainAuthenticated expects a stricter user
                  shape than CurrentUserState exposes. The runtime contract
                  has worked since the JS days; cast is a temporary bridge. */}
              <MainAuthenticated user={user as never} />
            </div>
            <Footer />
          </div>
          <OnboardingPopup />
        </SideMenuLayout>
      )}
    </>
  );
};

export default (withOktaAuth as unknown as (
  c: React.ComponentType<unknown>,
) => React.ComponentType<unknown>)(AuthenticatedApp as React.ComponentType<unknown>);
